
import { useCallback, useEffect, useState } from 'react';
import config from '../../../config';
import { admissionService } from '../../../services/admission';
import { getCookie } from '../../../services/cookies';
import wacomImage from '../../../svg/wacom.svg';

const useConsents = (
  reservation,
  patientToAdmission,
  setPatientToAdmission,
  currentStep,
  setCurrentStep,
  countryCode,
) => {
  const [loading, setLoading] = useState(false);
  const [patientConsents, setPatientConsents] = useState();
  const [windowInstances, setWindowInstances] = useState([]);
  const [disableSignButton, setDisableSignButton] = useState(false);

  const signers = {
    'basic': wacomImage,
  }

  const fetchPatientConsents = async () => {
    setLoading(true);

    let groupedConsents = [];

    patientToAdmission.prestations.forEach(prestation => {
      prestation.consents.map(type => {
        return (
          groupedConsents = [...groupedConsents, type]
        );
      });
    });

    groupedConsents = [...new Set(groupedConsents)].join();

    const response = await admissionService.getConsents({ types: groupedConsents });

    if (response.length > 0) {
      const addSignedKey = response.map(item => ({ ...item, 'signed': false, token: null }));
      setPatientToAdmission({ ...patientToAdmission, consents: addSignedKey });
      setPatientConsents(addSignedKey);
    }

    setLoading(false);
  };

  const confirmRefreshPage = (event) => {
    event.preventDefault();
    const message = '¿Estás seguro que deseas abandonar?';
    return event.returnValue = message;
  };

  const checkConsent = (checked, index) => {
    let _patientConsents = patientConsents;
    _patientConsents[index] = { ..._patientConsents[index], signed: checked, token: null, signerType: checked ? 'basic' : null };
    setPatientConsents([..._patientConsents]);
  };

  useEffect(() => {
    if (patientToAdmission) {
      if (patientToAdmission.consents) {
        setPatientConsents(patientToAdmission.consents);
      } else {
        fetchPatientConsents();
      }
    }
    window.addEventListener('beforeunload', confirmRefreshPage);

    return () => {
      window.removeEventListener('beforeunload', confirmRefreshPage);
    }
  }, [currentStep]);

  const addQueryParams = (url, params) => {
    const queryParams = Object.entries(params)
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    return `${url}?${queryParams}`;
  }

  const composeParams = (params) => {
    const allPrestrations = new Map();
    [...(patientToAdmission?.prestations || []), ...(patientToAdmission?.previousPrestations || [])]
      .forEach(prestation => {
        if (prestation && prestation.id) allPrestrations.set(prestation.id, prestation);
      });

    const prestations = Array.from(allPrestrations.values());
    const sortedPrestations = prestations
      ?.map(prestation => prestation.name)
      .sort((a, b) => a.localeCompare(b)) || [];

    const prestationNames = sortedPrestations.length > 1
      ? sortedPrestations.slice(0, -1).join(", ") + " y " + sortedPrestations.slice(-1)
      : sortedPrestations[0] || "";

    return {
      patientEmail: patientToAdmission.email,
      patientId: patientToAdmission.id,
      reservationId: reservation.id,
      companyDocumentNumber: reservation.company.business_document_number,
      companyDocumentType: reservation.company.business_document_type,
      companyName: params.companyName,
      userFirstName: patientToAdmission.userFirstName,
      userLastName: patientToAdmission.userLastName,
      profession: patientToAdmission.profession,
      userDocumentNumber: patientToAdmission.documentNumber,
      userDocumentType: patientToAdmission.documentType,
      dateBirth: patientToAdmission.dateBirth,
      phone: patientToAdmission.phone || 'Sin datos',
      signerType: 'basic',
      consentType: params.consentType,
      consentIndex: 0,
      consentStatus: params.consentStatus,
      from: params.from,
      branchOfficeId: getCookie("branch_office_id"),
      communeId: patientToAdmission.commune_id || '',
      address: patientToAdmission.address || '',
      evaluationType: patientToAdmission.evaluation_type,
      prestationNames
    };
  }

  const showConsentPopup = async (consentIndex, consentStatus, consentType, from) => {
    let sessionId;

    let companyName = encodeURIComponent(reservation.company.business_name);
    const paramsToSend = composeParams({ companyName, consentType, consentStatus, from, sessionId });

    let url = `${config.selfUrl}/consentimientos`;

    let target = '_blank';
    let options = `left=0,top=0,fullscreen=yes,scrollbars=no,status=no,location=no,toolbar=no,menubar=no,width=${window.innerWidth},height=${window.innerHeight}`;

    window.addEventListener('message', (event) => {
      if (url.startsWith(event.origin)) {
        if (event.data) {
          getConsentResult(event.data);
        }
      }
    }, false);
    let child = window.open(addQueryParams(url, paramsToSend), target, options);
    let instances = windowInstances;
    instances.push(child);
    setWindowInstances(instances);
  };

  const listenerBrowser = (url) => {
    window.addEventListener('message', (event) => {
      if (url.startsWith(event.origin)) {
        if (event.data && event.data.message && event.data.message.image) {
          getConsentResultBasic(event.data.message.image);
        }
      }
    }, false);
  }

  const showBasicSign = (consentIndex, consentStatus, consentType, from) => {
    let url = config.validatorUrl + '/basicSign';
    let target = '_blank';
    let options = `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=0,height=0,left=-1500,top=-1500`;

    listenerBrowser(url);
    let companyName = encodeURIComponent(reservation.company.business_name);
    const paramsToSend = composeParams({ companyName, consentType, consentIndex, consentStatus, from })
    window.open(addQueryParams(url, paramsToSend), target, options)
  }

  const getConsentResultBasic = (image, _patientConsents) => {
    setPatientToAdmission((_patientToAdmission) => ({ ..._patientToAdmission, consents: _patientConsents, signature_image: image, validation_token: null }));
  }

  const getConsentResult = (data) => {
    const popupData = {
      consentIndex: parseInt(data.consentIndex),
      consentStatus: data.consentStatus,
      token: null,
      consentingId: parseInt(data.consentingId),
    };
    let _patientConsents = patientConsents;
    _patientConsents[popupData.consentIndex] = {
      ..._patientConsents[popupData.consentIndex],
      signed: popupData.consentStatus,
      token: null,
      consentingId: parseInt(data.consentingId),
    };

    getConsentResultBasic(data.image, _patientConsents)
    setPatientConsents([..._patientConsents]);
  };

  const hasToDisableSignButton = useCallback(() => {
    const checkedConsent = patientConsents?.filter(consent => consent.signed);
    setDisableSignButton(checkedConsent?.length === 0);
    return;
  }, [patientConsents]);

  useEffect(() => {
    hasToDisableSignButton();
  }, [hasToDisableSignButton, patientConsents]);

  const handleNextStep = () => {
    const signedConsents = patientConsents.filter(consent => consent.signed);
    let _patientConsents = patientConsents;

    if (!signedConsents || signedConsents.length === 0) {
      _patientConsents = patientConsents.map(consent => {
        return { ...consent, signed: false, token: null };
      });
      setPatientConsents(_patientConsents);
    }

    setPatientToAdmission({ ...patientToAdmission, consents: _patientConsents });
    setCurrentStep(3);
  }

  return {
    loading,
    patientConsents,
    checkConsent,
    showConsentPopup,
    showBasicSign,
    handleNextStep,
    signers,
    disableSignButton,
  };
};

export default useConsents;
